export function createTranscriptStats({
  document,
  window,
  transcriptElements,
  numberFormatter,
}) {
  const statsElements = {
    container: document.getElementById("transcriptStats"),
    values: document.getElementById("transcriptStatsValues"),
    selection: document.getElementById("transcriptStatsSelection"),
  };

  const transcriptStatsSnapshot = {
    totalWords: 0,
    totalTokens: 0,
    selectionWords: 0,
    selectionTokens: 0,
  };

  let selectionUpdateHandle = null;
  let selectionClampRaf = null;
  let draggingFromGutter = false;

  const raf =
    (window &&
      window.requestAnimationFrame &&
      window.requestAnimationFrame.bind(window)) ||
    ((fn) => setTimeout(fn, 16));

  function countWords(text) {
    if (!text) return 0;
    const matches = text.match(/[A-Za-z0-9'_À-ɏЀ-ӿ-]+/g);
    return matches ? matches.length : 0;
  }

  function estimateTokensFromWords(words) {
    if (!words) return 0;
    return Math.max(0, Math.round(words / 0.75));
  }

  function formatStatNumber(value) {
    return numberFormatter.format(Math.max(0, Math.round(value)));
  }

  function updateStatsDisplay() {
    if (!statsElements.container) return;

    const stats = [
      {
        type: "words",
        label: `WORDS  ${formatStatNumber(transcriptStatsSnapshot.totalWords)}`,
      },
      {
        type: "tokens",
        label: `TOKENS ~${formatStatNumber(transcriptStatsSnapshot.totalTokens)}`,
      },
    ];

    statsElements.values.innerHTML = stats
      .map(
        (stat) =>
          `<span class="transcript-stat" data-stat="${stat.type}">${stat.label}</span>`,
      )
      .join("");

    if (transcriptStatsSnapshot.selectionWords > 0) {
      statsElements.selection.textContent = `SELECTED  ${formatStatNumber(transcriptStatsSnapshot.selectionWords)} WORDS (~${formatStatNumber(transcriptStatsSnapshot.selectionTokens)} TOKENS)`;
    } else {
      statsElements.selection.textContent = "";
    }
  }

  function updateTranscriptStats(content) {
    const totalWords = countWords(content);
    const totalTokens = estimateTokensFromWords(totalWords);

    transcriptStatsSnapshot.totalWords = totalWords;
    transcriptStatsSnapshot.totalTokens = totalTokens;
    transcriptStatsSnapshot.selectionWords = 0;
    transcriptStatsSnapshot.selectionTokens = 0;

    updateStatsDisplay();
    updateSelectionStats(true);
  }

  function getSelectionTextWithinTranscript() {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return "";

    const contentElement = transcriptElements.content;
    if (!contentElement) return "";

    let intersects = false;

    for (let index = 0; index < selection.rangeCount; index += 1) {
      const range = selection.getRangeAt(index);
      try {
        if (range && range.intersectsNode(contentElement)) {
          intersects = true;
          break;
        }
      } catch (error) {
        // Some browsers may throw if the node is detached; ignore and continue.
      }
    }

    if (!intersects) {
      return "";
    }

    return selection.toString();
  }

  // With gutters moved onto the content element via CSS,
  // selections naturally start inside the content. No heavy clamping needed.
  function selectTranscriptContent() {
    const contentElement = transcriptElements.content;
    if (!contentElement) return;

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(contentElement);

    selection.removeAllRanges();
    selection.addRange(range);
  }

  function updateSelectionStats(forceReset = false) {
    if (forceReset) {
      transcriptStatsSnapshot.selectionWords = 0;
      transcriptStatsSnapshot.selectionTokens = 0;
      updateStatsDisplay();
      return;
    }

    if (!document.body.classList.contains("showing-transcript")) {
      return;
    }

    const selectedText = getSelectionTextWithinTranscript();
    if (!selectedText) {
      transcriptStatsSnapshot.selectionWords = 0;
      transcriptStatsSnapshot.selectionTokens = 0;
    } else {
      const selectedWords = countWords(selectedText);
      transcriptStatsSnapshot.selectionWords = selectedWords;
      transcriptStatsSnapshot.selectionTokens =
        estimateTokensFromWords(selectedWords);
    }

    updateStatsDisplay();
  }

  function scheduleSelectionUpdate() {
    if (selectionUpdateHandle) {
      clearTimeout(selectionUpdateHandle);
    }
    selectionUpdateHandle = setTimeout(() => {
      selectionUpdateHandle = null;
      updateSelectionStats();
    }, 150);
  }

  function handleGlobalSelectionChange() {
    if (!document.body.classList.contains("showing-transcript")) return;
    if (selectionClampRaf == null) {
      selectionClampRaf = raf(() => {
        selectionClampRaf = null;
        scheduleSelectionUpdate();
      });
    }
  }

  function getContentRect() {
    const el = transcriptElements.content;
    return el ? el.getBoundingClientRect() : null;
  }

  function caretRangeFromPointWithinContent(x, y) {
    const content = transcriptElements.content;
    if (!content) return null;
    const rect = content.getBoundingClientRect();
    // Clamp the point inside the content box so caret placement is valid
    const px = Math.min(Math.max(x, rect.left + 1), rect.right - 1);
    const py = Math.min(Math.max(y, rect.top + 1), rect.bottom - 1);

    const doc = document;
    let range = null;
    if (doc.caretRangeFromPoint) {
      range = doc.caretRangeFromPoint(px, py);
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(px, py);
      if (pos) {
        range = doc.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
    return range;
  }

  function onBodyMouseDown(event) {
    if (!document.body.classList.contains("showing-transcript")) return;
    if (event.button !== 0) return; // only primary button
    const bodyWrapper = transcriptElements.bodyWrapper;
    const viewport = transcriptElements.body;
    const content = transcriptElements.content;
    if (!bodyWrapper || !viewport || !content) return;

    const insideBody = bodyWrapper.contains(event.target);
    const insideContent = content.contains(event.target);
    if (!insideBody || insideContent) return; // normal selection inside content

    // Start selection anchored inside content at the pointer's vertical position
    const range = caretRangeFromPointWithinContent(
      event.clientX,
      event.clientY,
    );
    if (!range) return;
    event.preventDefault();
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);

    draggingFromGutter = true;
    document.addEventListener("mousemove", onBodyMouseMove, true);
    document.addEventListener("mouseup", onBodyMouseUp, true);
  }

  function onBodyMouseMove(event) {
    if (!draggingFromGutter) return;
    const range = caretRangeFromPointWithinContent(
      event.clientX,
      event.clientY,
    );
    const sel = window.getSelection();
    if (!range || !sel) return;
    if (typeof sel.extend === "function") {
      sel.extend(range.startContainer, range.startOffset);
    } else {
      const anchorNode = sel.anchorNode;
      const anchorOffset = sel.anchorOffset;
      const newRange = document.createRange();
      newRange.setStart(anchorNode, anchorOffset);
      newRange.setEnd(range.startContainer, range.startOffset);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
    scheduleSelectionUpdate();
  }

  function onBodyMouseUp() {
    if (!draggingFromGutter) return;
    draggingFromGutter = false;
    document.removeEventListener("mousemove", onBodyMouseMove, true);
    document.removeEventListener("mouseup", onBodyMouseUp, true);
  }

  function toggleTranscriptStats() {}

  function handleStatsHotkeys(event) {
    if (!document.body.classList.contains("showing-transcript")) return;
    if (event.defaultPrevented) return;
    const target = event.target;
    if (
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable)
    ) {
      return;
    }

    if (
      (event.key === "a" || event.key === "A") &&
      (event.ctrlKey || event.metaKey)
    ) {
      event.preventDefault();
      selectTranscriptContent();
      updateSelectionStats();
      return;
    }

    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey)
      return;

    if (event.key === "s" || event.key === "S") {
      toggleTranscriptStats();
      event.preventDefault();
    }
  }

  document.addEventListener("selectionchange", handleGlobalSelectionChange);
  document.addEventListener("mousedown", onBodyMouseDown, true);
  document.addEventListener("keydown", handleStatsHotkeys);

  return { updateTranscriptStats, updateSelectionStats };
}
